import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import callConstants from "constants/Call";
import { formatError } from "helpers/formatError";
import CampaignTargetCallService from "services/CampaignTargetCall";

const STATUS = callConstants.TRANSCRIPT_STATUSES;
const ROLES = callConstants.SPEAKER_ROLES;
const IN_PROGRESS = new Set(callConstants.TRANSCRIPT_STATUSES_IN_PROGRESS);
const POLL_INTERVAL_MS = 5000;

const isEnglish = code => Boolean(code) && code.startsWith("en");

/** Both parties were identified, so exchanging them is meaningful. */
const bothRolesResolved = speakers => {
  const roles = new Set(Object.values(speakers ?? {}).map(entry => entry.role));
  return roles.has(ROLES.REP) && roles.has(ROLES.CONTACT);
};

/**
 * Loads a call's transcript, polling while transcription is still running so an
 * open dialog fills itself in without the user reopening it.
 */
export function useCallTranscript(callId, { enabled = true } = {}) {
  const queryKey = ["call-transcript", callId];
  const queryClient = useQueryClient();

  const {
    data: transcript,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => CampaignTargetCallService.getTranscript(callId),
    enabled: enabled && callId != null,
    retry: false,
    refetchInterval: query =>
      IN_PROGRESS.has(query.state.data?.status) ? POLL_INTERVAL_MS : false,
  });

  const { mutate: swapSpeakers } = useMutation({
    mutationFn: swapped =>
      CampaignTargetCallService.swapTranscriptSpeakers(callId, swapped),
    onSuccess: updated => queryClient.setQueryData(queryKey, updated),
    onError: err => toast.error(formatError(err, "Failed to swap speakers")),
  });

  const status = transcript?.status;
  const turns = transcript?.turns?.turns ?? [];
  const turnsEn = transcript?.turns_en?.turns ?? [];
  const sourceLanguage = transcript?.turns?.source_language;

  return {
    transcript,
    status,
    isLoading,
    error,
    turns,
    turnsEn,
    speakers: transcript?.speakers,
    speakersSwapped: Boolean(transcript?.speakers_swapped),
    canSwapSpeakers: bothRolesResolved(transcript?.speakers),
    swapSpeakers,
    isInProgress: IN_PROGRESS.has(status),
    isFailed: status === STATUS.FAILED,
    hasTranslation: turnsEn.length > 0,
    translatedFrom: transcript?.turns_en?.translated_from,
    // A finished non-English call with no English view had its translation fail,
    // which is worth saying rather than quietly showing only the original.
    translationFailed:
      status === STATUS.COMPLETED &&
      turns.length > 0 &&
      turnsEn.length === 0 &&
      !isEnglish(sourceLanguage),
    isEmpty: status === STATUS.COMPLETED && turns.length === 0,
  };
}
